import { GridRowVisibilityApi } from "@components/DataGrid/models/gridRowVisibilityApi";
import { GridRowVisibilitySelectionModel } from "@components/DataGrid/models/gridRowVisibilitySelectionModel";
import { GridEventListener, gridExpandedSortedRowIdsSelector, gridPaginatedVisibleSortedGridRowIdsSelector, GridRowId, gridRowMaximumTreeDepthSelector, gridRowTreeSelector, useGridApiEventHandler, useGridLogger, useGridSelector } from "@mui/x-data-grid-premium";
import { GridPrivateApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { DataGridProcessedProps, useGridApiMethod, useGridVisibleRows } from "@mui/x-data-grid/internals";
import { MutableRefObject, useCallback, useMemo, useRef } from "react";
import "../../../models/dataGridProps.ts";
import { gridRowVisibilitySelectionStateSelector, visibilityGridRowsSelector, visibilityIdsLookupSelector } from "./gridRowVisibilitySelector";
import { findRowsToDeselect, findRowsToSelect } from "./utils";

const getVisibilitySelectionModelPropValue = (
  visibilitySelectionModelProp: DataGridProcessedProps['rowVisibilitySelectionModel'],
  prevRowVisibilitySelection?: GridRowVisibilitySelectionModel,
) => {
  if (visibilitySelectionModelProp == null) {
    return visibilitySelectionModelProp;
  }

  if (Array.isArray(visibilitySelectionModelProp)) {
    return visibilitySelectionModelProp;
  }

  if (prevRowVisibilitySelection && prevRowVisibilitySelection[0] === visibilitySelectionModelProp) {
    return prevRowVisibilitySelection;
  }

  return [visibilitySelectionModelProp];
};

// TODO: why do we pass the rootProps as a parameter instead of using the useRootProps hook?
export const useGridRowVisibilitySelection = (
  apiRef: MutableRefObject<GridPrivateApiPremium>,
  props: Pick<
    DataGridProcessedProps,
    | 'pagination'
    | 'paginationMode'
    | 'rowVisibilitySelection'
    | 'rowVisibilitySelectionPropagation'
    | 'rowVisibilitySelectionModel'
    | 'onRowVisibilitySelectionModelChange'
  >,
): void => {
  const logger = useGridLogger(apiRef, 'useGridRowVisibilitySelection');

  const runIfRowSelectionIsEnabled = useCallback(
    <Args extends any[]>(callback: (...args: Args) => void) =>
      (...args: Args) => {
        if (props.rowVisibilitySelection) {
          callback(...args);
        }
      },
    [props.rowVisibilitySelection],
  );

  const applyAutoSelection = (props.rowVisibilitySelectionPropagation?.parents || props.rowVisibilitySelectionPropagation?.descendants);

  const propRowVisibilitySelectionModel = useMemo(() => {
    return getVisibilitySelectionModelPropValue(
      props.rowVisibilitySelectionModel,
      gridRowVisibilitySelectionStateSelector(apiRef.current.state),
    );
  }, [apiRef, props.rowVisibilitySelectionModel]);

  apiRef.current.registerControlState({
    stateId: 'rowVisibilitySelection',
    propModel: propRowVisibilitySelectionModel,
    propOnChange: props.onRowVisibilitySelectionModelChange,
    stateSelector: gridRowVisibilitySelectionStateSelector,
    changeEvent: 'rowVisibilitySelectionChange' as any, // TODO: see dataGridPops.ts GridControlledStateEventLookup
  });

  const lastRowToggled = useRef<GridRowId | null>(null);

  const canHaveMultipleSelection = true; /*isMultipleRowSelectionEnabled(props);*/
  const visibleRows = useGridVisibleRows(apiRef, props);
  const tree = useGridSelector(apiRef, gridRowTreeSelector);
  const isNestedData = useGridSelector(apiRef, gridRowMaximumTreeDepthSelector) > 1;

  const expandMouseRowRangeSelection = useCallback(
    (id: GridRowId) => {
      let endId = id;
      const startId = lastRowToggled.current ?? id;
      const isSelected = apiRef.current.isRowVisibilitySelected(id);
      if (isSelected) {
        const visibleRowIds = gridExpandedSortedRowIdsSelector(apiRef);
        const startIndex = visibleRowIds.findIndex((rowId) => rowId === startId);
        const endIndex = visibleRowIds.findIndex((rowId) => rowId === endId);
        if (startIndex === endIndex) {
          return;
        }
        if (startIndex > endIndex) {
          endId = visibleRowIds[endIndex + 1];
        } else {
          endId = visibleRowIds[endIndex - 1];
        }
      }

      lastRowToggled.current = id;

      apiRef.current.setRowRangeVisibility({ startId, endId }, !isSelected);
    },
    [apiRef],
  );

  /*
    * EVENTS
    */
  const handleHeaderVisibilitySelectionCheckboxChange = useCallback<
    GridEventListener<'headerVisibilitySelectionCheckboxChange'>
  >(
    (params) => {
      const rowsToBeSelected =
        props.pagination /*&& props.checkboxSelectionVisibleOnly*/ && props.paginationMode === 'client'
          ? gridPaginatedVisibleSortedGridRowIdsSelector(apiRef)
          : gridExpandedSortedRowIdsSelector(apiRef);

      apiRef.current.setRowsVisibility(rowsToBeSelected, params.value);
    },
    [apiRef, /*props.checkboxSelectionVisibleOnly,*/ props.pagination, props.paginationMode],
  );

  const handleRowVisibilityCheckboxChange = useCallback<
    GridEventListener<'rowVisibilityCheckboxChange'>
  >(
    (params, event) => {
      if ((event.nativeEvent as any).shiftKey) {
        expandMouseRowRangeSelection(params.id);
      } else {
        apiRef.current.setRowVisibility(params.id, params.value, false);
      }
    },
    [apiRef, expandMouseRowRangeSelection],
  );

  /*
   * API METHODS
   */
  const isRowVisibilitySelected = useCallback<GridRowVisibilityApi['isRowVisibilitySelected']>(
    (id) => gridRowVisibilitySelectionStateSelector(apiRef.current.state).includes(id),
    [apiRef],
  );

  const setRowVisibility = useCallback<GridRowVisibilityApi['setRowVisibility']>(
    (id, isVisible = true, resetVisibility = false) => {
      //if (!apiRef.current.isRowVisibilityImmutable(id)) {
      //  return;
      //}

      lastRowToggled.current = id;

      if (resetVisibility) {
        logger.debug(`Setting visibility for row ${id}`);

        const newVisibility: GridRowId[] = [];
        const addRow = (rowId: GridRowId) => {
          newVisibility.push(rowId);
        };
        if (isVisible) {
          addRow(id);
          if (applyAutoSelection) {
            findRowsToSelect(
              apiRef,
              tree,
              id,
              props.rowVisibilitySelectionPropagation?.descendants ?? false,
              props.rowVisibilitySelectionPropagation?.parents ?? false,
              addRow,
            );
          }
        }

        apiRef.current.setRowVisibilityModel(newVisibility);
      } else {
        logger.debug(`Toggling visibility selection for row ${id}`);

        const visibility = gridRowVisibilitySelectionStateSelector(apiRef.current.state);

        const newVisibility: Set<GridRowId> = new Set(visibility);
        newVisibility.delete(id);

        const addRow = (rowId: GridRowId) => {
          newVisibility.add(rowId);
        };
        const removeRow = (rowId: GridRowId) => {
          newVisibility.delete(rowId);
        };

        if (isVisible) {
          addRow(id);
          if (applyAutoSelection) {
            findRowsToSelect(
              apiRef,
              tree,
              id,
              props.rowVisibilitySelectionPropagation?.descendants ?? false,
              props.rowVisibilitySelectionPropagation?.parents ?? false,
              addRow,
            );
          }
        } else if (applyAutoSelection) {
          findRowsToDeselect(
            apiRef,
            tree,
            id,
            props.rowVisibilitySelectionPropagation?.descendants ?? false,
            props.rowVisibilitySelectionPropagation?.parents ?? false,
            removeRow,
          );
        }

        const isSelectionValid = newVisibility.size < 2 || canHaveMultipleSelection;
        if (isSelectionValid) {
          apiRef.current.setRowVisibilityModel(Array.from(newVisibility));
        }
      }
    },
    [
      apiRef,
      logger,
      applyAutoSelection,
      tree,
      props.rowVisibilitySelectionPropagation?.descendants,
      props.rowVisibilitySelectionPropagation?.parents,
      canHaveMultipleSelection,
    ],
  );

  const setRowsVisibility = useCallback<GridRowVisibilityApi['setRowsVisibility']>(
    (ids: GridRowId[], isVisible = true, resetVisibility = false) => {
      logger.debug(`Setting visibility selection for several rows`);

      const selectableIds = ids; /*ids.filter((id) => apiRef.current.isRowSelectable(id));*/

      let newVisibility: Set<GridRowId>;
      if (resetVisibility) {
        if (isVisible) {
          newVisibility = new Set(selectableIds);
          if (applyAutoSelection) {
            const addRow = (rowId: GridRowId) => {
              newVisibility.add(rowId);
            };
            selectableIds.forEach((id) => {
              findRowsToSelect(
                apiRef,
                tree,
                id,
                props.rowVisibilitySelectionPropagation?.descendants ?? false,
                props.rowVisibilitySelectionPropagation?.parents ?? false,
                addRow,
              );
            });
          }
        } else {
          newVisibility = new Set();
        }
        const currentLookup = visibilityIdsLookupSelector(apiRef);
        if (
          newVisibility.size === Object.keys(currentLookup).length &&
          Array.from(newVisibility).every((id) => currentLookup[id] === id)
        ) {
          return;
        }
      } else {
        newVisibility = new Set(Object.values(visibilityIdsLookupSelector(apiRef)));
        const addRow = (rowId: GridRowId) => {
          newVisibility.add(rowId);
        };
        const removeRow = (rowId: GridRowId) => {
          newVisibility.delete(rowId);
        };

        selectableIds.forEach((id) => {
          if (isVisible) {
            newVisibility.add(id);
            if (applyAutoSelection) {
              findRowsToSelect(
                apiRef,
                tree,
                id,
                props.rowVisibilitySelectionPropagation?.descendants ?? false,
                props.rowVisibilitySelectionPropagation?.parents ?? false,
                addRow,
              );
            }
          } else {
            removeRow(id);
            if (applyAutoSelection) {
              findRowsToDeselect(
                apiRef,
                tree,
                id,
                props.rowVisibilitySelectionPropagation?.descendants ?? false,
                props.rowVisibilitySelectionPropagation?.parents ?? false,
                removeRow,
              );
            }
          }
        });
      }

      const isSelectionValid = newVisibility.size < 2 || canHaveMultipleSelection;
      if (isSelectionValid) {
        apiRef.current.setRowVisibilityModel(Array.from(newVisibility));
      }
    },
    [
      logger,
      applyAutoSelection,
      canHaveMultipleSelection,
      apiRef,
      tree,
      props.rowVisibilitySelectionPropagation?.descendants,
      props.rowVisibilitySelectionPropagation?.parents,
    ],
  );

  const setRowVisibilityModel = useCallback<GridRowVisibilityApi['setRowVisibilityModel']>(
    (model) => {
      const currentModel = gridRowVisibilitySelectionStateSelector(apiRef.current.state);
      if (currentModel !== model) {
        logger.debug(`Setting visibility model`);
        apiRef.current.setState((state: any) => ({
          ...state,
          rowVisibilitySelection: props.rowVisibilitySelection ? model : [],
        }));
        apiRef.current.forceUpdate();
      }
    },
    [apiRef, logger, props.rowVisibilitySelection, canHaveMultipleSelection],
  );

  const setRowRangeVisibility = useCallback<GridRowVisibilityApi['setRowRangeVisibility']>(
    (
      {
        startId,
        endId,
      }: {
        startId: GridRowId;
        endId: GridRowId;
      },
      isVisible = true,
      resetVisibility = false,
    ) => {
      if (!apiRef.current.getRow(startId) || !apiRef.current.getRow(endId)) {
        return;
      }

      logger.debug(`Expanding visibility selection from row ${startId} to row ${endId}`);

      // Using rows from all pages allow to select a range across several pages
      const allPagesRowIds = gridExpandedSortedRowIdsSelector(apiRef);
      const startIndex = allPagesRowIds.indexOf(startId);
      const endIndex = allPagesRowIds.indexOf(endId);
      const [start, end] = startIndex > endIndex ? [endIndex, startIndex] : [startIndex, endIndex];
      const rowsBetweenStartAndEnd = allPagesRowIds.slice(start, end + 1);
      apiRef.current.setRowsVisibility(rowsBetweenStartAndEnd, isVisible, resetVisibility);
    },
    [apiRef, logger],
  );

  const getVisibleRows = useCallback<GridRowVisibilityApi['getVisibleRows']>(
    () => visibilityGridRowsSelector(apiRef),
    [apiRef],
  );

  const visibilityPublicApi: GridRowVisibilityApi = {
    isRowVisibilitySelected,
    setRowVisibility,
    setRowsVisibility,
    setRowRangeVisibility,
    getVisibleRows,
    setRowVisibilityModel,
  };

  useGridApiMethod(apiRef, visibilityPublicApi, 'public');

  useGridApiEventHandler(
    apiRef,
    'rowVisibilityCheckboxChange',
    runIfRowSelectionIsEnabled(handleRowVisibilityCheckboxChange),
  );
  useGridApiEventHandler(
    apiRef,
    'headerVisibilitySelectionCheckboxChange',
    handleHeaderVisibilitySelectionCheckboxChange,
  );
};
